import { useState } from 'react';
import { AlertTriangle, CalendarClock, FileWarning, Search } from 'lucide-react';

import { ClearFiltersButton } from '@/ui/ClearFiltersButton';
import { DateRangeFilter } from '@/ui/DateRangeFilter';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { FilterPresets, type FilterPreset } from '@/ui/FilterPresets';
import { MobileCardList, type ItemDoCard } from '@/ui/MobileCardList';
import { MonthFilter } from '@/ui/MonthFilter';
import { PaginacaoCompacta } from '@/ui/PaginacaoCompacta';
import { StatusBadge } from '@/ui/StatusBadge';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { Button } from '@/ui/base/button';

import { ComponentShowcase } from '../ComponentShowcase';
import { CREDENCIADOS_DE_EXEMPLO, OS_DE_EXEMPLO } from '../exemplos';

/** Competência do mês corrente, calculada: a amostra não envelhece. */
function competenciaDeHoje(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

const ATALHOS: readonly FilterPreset[] = [
  {
    id: 'crmc-vencido',
    label: 'CRMC vencido',
    icon: <FileWarning className="h-3.5 w-3.5" aria-hidden="true" />,
    values: { documento: 'crmc', situacao: 'vencido' },
    description: 'Condutores com o CRMC vencido',
  },
  {
    id: 'seguro-30-dias',
    label: 'Seguro vence em 30 dias',
    icon: <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />,
    values: { documento: 'seguro', situacao: 'vence-30' },
    description: 'Veículos cujo seguro vence nos próximos 30 dias',
  },
  {
    id: 'bloqueados',
    label: 'Bloqueados',
    icon: <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />,
    values: { situacao: 'bloqueado' },
  },
];

/** As amostras de listagem que a aba Componentes do sistema mostra depois da DataTable. */
export function AmostrasDeListagem() {
  const [aberto, definirAberto] = useState<string | null>(null);
  const [pagina, definirPagina] = useState(1);
  const [filtros, definirFiltros] = useState<Record<string, string>>({ situacao: 'bloqueado' });
  const [periodo, definirPeriodo] = useState<DateRangeValue>({ preset: 'LAST_7' });
  const [intervalo, definirIntervalo] = useState({ from: '', to: '' });
  const [competencia, definirCompetencia] = useState(competenciaDeHoje);

  const itens: ItemDoCard[] = [
    {
      id: 'com-clique',
      title: OS_DE_EXEMPLO.numero,
      subtitle: `Condutor ${OS_DE_EXEMPLO.condutor}`,
      badges: <StatusBadge status="ativo" label="Vigente" />,
      fields: [
        { label: 'Placa', value: OS_DE_EXEMPLO.placa },
        { label: 'Monitor', value: OS_DE_EXEMPLO.monitor },
      ],
      onClick: () => definirAberto(OS_DE_EXEMPLO.numero),
    },
    {
      id: 'com-acao',
      title: CREDENCIADOS_DE_EXEMPLO[1]!.nome,
      subtitle: `DRE ${CREDENCIADOS_DE_EXEMPLO[1]!.dre}`,
      badges: <StatusBadge status={CREDENCIADOS_DE_EXEMPLO[1]!.status} />,
      fields: [{ label: 'CPF', value: CREDENCIADOS_DE_EXEMPLO[1]!.cpf, full: true }],
      actions: (
        <Button size="sm" variant="outline" onClick={() => definirAberto('ação do cartão')}>
          Cobrar documentos
        </Button>
      ),
      onClick: () => definirAberto(CREDENCIADOS_DE_EXEMPLO[1]!.nome),
    },
  ];

  const { from, to } = resolveDateRange(periodo);
  const filtrosAtivos = Object.values(filtros).filter(Boolean).length;

  return (
    <>
      <ComponentShowcase
        id="mobile-card-list"
        title="MobileCardList"
        description="Lista em cartões para o celular: a tela esconde a DataTable abaixo de 640 px (hidden sm:block) e mostra esta (sm:hidden). Teclado: cartão com onClick e sem controle focável por dentro vira botão (Tab, Enter ou Espaço); cartão que já traz um controle — um link no título, um botão em actions — não vira botão (seriam dois controles aninhados), o teclado usa esse controle e o mouse continua abrindo pelo cartão inteiro. Clique em actions não abre o cartão."
        code={`<div className="hidden sm:block"><DataTable … /></div>
<MobileCardList
  className="sm:hidden"
  isLoading={consulta.isPending}
  items={ordens.map((os) => ({
    id: os.id,
    title: os.numero,
    subtitle: os.condutor,
    badges: <StatusBadge status={os.situacao} />,
    fields: [{ label: 'Placa', value: os.placa }],
    onClick: () => navigate(\`/credenciado/ordens-de-servico/\${os.id}\`),
  }))}
  emptyTitle="Nenhuma Ordem de Serviço"
/>`}
        propsTable={[
          [
            'items',
            'ItemDoCard[]',
            '-',
            'Cartões: id, title, subtitle?, badges?, fields?, actions?, onClick?, cardClassName?',
          ],
          ['isLoading', 'boolean', 'false', 'Mostra o indicador de carregando no lugar da lista'],
          ['emptyIcon', 'LucideIcon', 'Inbox', 'Ícone do estado vazio'],
          ['emptyTitle', 'string', '"Nenhum registro"', 'Título do estado vazio'],
          ['emptyDescription', 'string', '-', 'Texto de apoio do estado vazio'],
          ['className', 'string', '-', 'Classes do contêiner (ex.: sm:hidden)'],
        ]}
      >
        <div className="w-full max-w-sm space-y-2">
          <MobileCardList items={itens} />
          <p className="text-xs text-muted-foreground" role="status" aria-live="polite">
            {aberto ? `Abriria: ${aberto}` : 'Clique ou tecle Enter num cartão.'}
          </p>
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="paginacao-compacta"
        title="PaginacaoCompacta"
        description="Paginação da lista em cartões do celular: anterior, posição e próxima, sem o seletor de quantidade nem os números do DataPagination, que não cabem em 375 px. Com uma página só, não aparece. A paginação continua no servidor."
        code={`<PaginacaoCompacta pagina={pagina} tamanho={25} total={total} aoMudar={setPagina} />`}
        propsTable={[
          ['pagina', 'number', '-', 'Página atual, base 1'],
          ['tamanho', 'number', '-', 'Itens por página'],
          ['total', 'number', '-', 'Total de registros (do servidor)'],
          ['aoMudar', '(pagina: number) => void', '-', 'Chamado por Anterior e Próxima'],
        ]}
      >
        <div className="w-full max-w-sm">
          <PaginacaoCompacta pagina={pagina} tamanho={25} total={127} aoMudar={definirPagina} />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="filter-presets"
        title="FilterPresets"
        description="Atalhos de filtro: as perguntas que a equipe faz todo dia, a um clique. O atalho substitui os filtros atuais (não acumula) e fica marcado quando os valores batem. Contagem só se vier de dado real."
        code={`<FilterPresets
  presets={[{ id: 'bloqueados', label: 'Bloqueados', values: { situacao: 'bloqueado' } }]}
  currentValues={filtros}
  onApply={setFiltros}
/>`}
        propsTable={[
          [
            'presets',
            'readonly FilterPreset[]',
            '-',
            'Atalhos: id, label, icon?, values, badge?, description?',
          ],
          [
            'currentValues',
            'Record<string, string>',
            '-',
            'Filtros atuais; revelam o atalho ativo',
          ],
          ['onApply', '(values) => void', '-', 'Recebe a combinação do atalho escolhido'],
          ['label', 'string', '-', 'Nome do grupo de atalhos'],
          ['className', 'string', '-', 'Classes do contêiner'],
        ]}
      >
        <FilterPresets presets={ATALHOS} currentValues={filtros} onApply={definirFiltros} />
      </ComponentShowcase>

      <ComponentShowcase
        id="clear-filters-button"
        title="ClearFiltersButton"
        description="Limpa os filtros da busca. Sem filtro ativo, o botão não existe (regra 7: não fica desabilitado). Com contagem, diz o que o clique faz: “Limpar 1 filtro aplicado”."
        code={`<ClearFiltersButton active={ativos > 0} count={ativos} onClear={limpar} />`}
        propsTable={[
          ['active', 'boolean', '-', 'Há filtro ativo; falso = não renderiza'],
          ['count', 'number', '-', 'Quantidade de filtros ativos, dentro da frase'],
          ['onClear', '() => void', '-', 'Limpa os filtros'],
          ['className', 'string', '-', 'Classes do botão'],
        ]}
      >
        <div className="flex flex-wrap items-center gap-3">
          <ClearFiltersButton
            active={filtrosAtivos > 0}
            count={filtrosAtivos}
            onClear={() => definirFiltros({})}
          />
          {filtrosAtivos === 0 && (
            <Button variant="outline" size="sm" onClick={() => definirFiltros({ situacao: 'x' })}>
              <Search className="h-4 w-4" aria-hidden="true" /> Aplicar um filtro
            </Button>
          )}
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="date-range-preset-filter"
        title="DateRangePresetFilter"
        description="Período com predefinições (Todos, Hoje, Últimos e Próximos 7/15/30 dias, Este mês, Último mês, Este ano, Data específica, Período personalizado). O valor guarda a escolha; resolveDateRange devolve os limites inclusivos em ISO para a consulta. É o único filtro de período com atalhos: as cópias locais saíram."
        code={`import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';

const [periodo, setPeriodo] = useState<DateRangeValue>({ preset: 'ALL' });
const { from, to } = resolveDateRange(periodo); // vai para a API

<DateRangePresetFilter label="Período" value={periodo} onChange={setPeriodo} />`}
        propsTable={[
          ['label', 'string', '-', 'Rótulo visível e nome acessível do seletor'],
          ['value', 'DateRangeValue', '-', '{ preset, date?, from?, to? } em ISO yyyy-MM-dd'],
          ['onChange', '(value) => void', '-', 'Ao trocar de predefinição, limpa o que não é dela'],
          ['width', 'string', '"w-full sm:w-[200px]"', 'Largura do gatilho'],
          ['hideYearOption', 'boolean', 'false', 'Esconde "Este ano"'],
          ['hideCustomRange', 'boolean', 'false', 'Esconde "Período personalizado"'],
        ]}
      >
        <div className="flex flex-col gap-2">
          <DateRangePresetFilter label="Período" value={periodo} onChange={definirPeriodo} />
          <p className="font-mono text-xs text-muted-foreground">
            resolveDateRange → from: {from ?? '—'} · to: {to ?? '—'}
          </p>
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="date-range-filter"
        title="DateRangeFilter"
        description="Intervalo preciso, de uma data a outra, com rótulo em cada campo; a data final não pode ser anterior à inicial (o próprio campo impede). Para atalho rápido, o DateRangePresetFilter."
        code={`<DateRangeFilter from={de} to={ate} onChange={({ from, to }) => { setDe(from); setAte(to); }} />`}
        propsTable={[
          ['from', 'string', '-', 'Data inicial ISO; vazio = sem limite'],
          ['to', 'string', '-', 'Data final ISO; vazio = sem limite'],
          ['onChange', '({ from, to }) => void', '-', 'Recebe o intervalo inteiro'],
          ['label', 'string', '"Período"', 'Rótulo do grupo'],
          ['className', 'string', '-', 'Classes do contêiner'],
        ]}
      >
        <DateRangeFilter from={intervalo.from} to={intervalo.to} onChange={definirIntervalo} />
      </ComponentShowcase>

      <ComponentShowcase
        id="month-filter"
        title="MonthFilter"
        description="Competência mensal (yyyy-mm): atalhos dos últimos meses e grade de mês e ano; mês futuro não se escolhe. O nome acessível junta o rótulo e o valor visível (“Competência out/26”)."
        code={`<MonthFilter value={competencia} onChange={setCompetencia} />`}
        propsTable={[
          ['value', 'string', '-', 'Competência yyyy-mm; vazio = Todos'],
          ['onChange', '(competencia: string) => void', '-', 'Recebe a competência escolhida'],
          ['label', 'string', '"Competência"', 'Rótulo'],
          ['id', 'string', '"competencia"', 'Id do gatilho (um por página)'],
          ['hideLabel', 'boolean', 'false', 'Rótulo só para leitor de tela'],
          ['mesesRecentes', 'number', '6', 'Quantos meses recentes viram atalho'],
          ['className', 'string', '-', 'Classes do contêiner'],
        ]}
      >
        <MonthFilter id="ds-competencia" value={competencia} onChange={definirCompetencia} />
      </ComponentShowcase>
    </>
  );
}
